/*
 * 初期化計測ユーティリティ
 * - TTFD: ドキュメント取得（IndexedDB/Firestore などローカル復元完了）
 * - TTS : provider.sync（初回同期完了）
 * - TTE : editor.onCreate 到達（可編集になった瞬間）
 * - TTR : 主要プラグイン ready
 */

export type InitTimingEventName = 'TTFD' | 'TTS_PRE' | 'TTS' | 'TTE' | 'TTR' | 'FS_APPLY' | 'WDG_READY' | 'WDG_START' | 'TAG_WATCH_READY' | 'TAG_FIRST_EVENT' | 'LINK_WATCH_READY' | 'LINK_FIRST_EVENT' | 'EDIT_LOCKED' | 'EDIT_UNLOCKED' | 'INIT_SUMMARY' | 'EDITOR_DESTROYED';

export interface InitTimingContext {
  workspaceId: string;
  boardId: string;
  visitType: 'first' | 'return' | 'offline' | 'unknown';
}

export interface InitTimingPayload extends InitTimingContext {
  t: number; // performance.now()
  sinceStart: number; // セッション開始からの経過ms
  docSize?: number;
  pluginName?: string;
  extra?: Record<string, unknown>;
}

class InitTimingTracker {
  private startTs: number | null = null;
  private context: InitTimingContext | null = null;
  private eventTimes: Record<string, number> = {};

  start(context: InitTimingContext) {
    this.context = context;
    this.startTs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    // セッション開始ログ
    this.log('SESSION_START', { ...context, t: this.now(), sinceStart: 0 });
    this.eventTimes = {};
  }

  mark(event: InitTimingEventName, payload?: Partial<InitTimingPayload>) {
    const base = this.buildPayload(payload);
    this.eventTimes[event] = base.sinceStart;
    this.log(event, base);
  }

  markPluginReady(pluginName: string, payload?: Partial<InitTimingPayload>) {
    const base = this.buildPayload({ ...payload, pluginName });
    this.log('TTR', base);
  }

  private buildPayload(payload?: Partial<InitTimingPayload>): InitTimingPayload {
    const t = this.now();
    const sinceStart = this.startTs ? Math.max(0, t - this.startTs) : 0;
    const ctx = this.context ?? { workspaceId: 'unknown', boardId: 'unknown', visitType: 'unknown' as const };
    return {
      ...ctx,
      t,
      sinceStart,
      docSize: payload?.docSize,
      pluginName: payload?.pluginName,
      extra: payload?.extra,
    };
  }

  summarize(extra?: Record<string, unknown>) {
    const summary = {
      ttfdMs: this.eventTimes['TTFD'] ?? null,
      ttsPreMs: this.eventTimes['TTS_PRE'] ?? null,
      ttsMs: this.eventTimes['TTS'] ?? null,
      tteMs: this.eventTimes['TTE'] ?? null,
      ttrMs: this.eventTimes['TTR'] ?? null,
      totalInitMs: this.eventTimes['TTR'] ?? this.eventTimes['TTS'] ?? this.eventTimes['TTE'] ?? null,
      events: { ...this.eventTimes },
      ...extra,
    };
    const base = this.buildPayload({ extra: summary });
    this.log('INIT_SUMMARY', base);
  }

  private now(): number {
    return (typeof performance !== 'undefined' ? performance.now() : Date.now());
  }

  // 現状はconsole出力。将来は分析基盤へ送信できるようオプションを追加
  // eslint-disable-next-line class-methods-use-this
  private log(event: string, payload: any) {
    try {} catch (_) {}
  }
}

export const initTiming = new InitTimingTracker();

// ヘルパー: 訪問タイプ推定
export function detectVisitType(hasLocalData: boolean, isOnline: boolean): InitTimingContext['visitType'] {
  if (!isOnline) return 'offline';
  if (hasLocalData) return 'return';
  return 'first';
}


